import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GripVertical, Trash2 } from "lucide-react";
import { slugify } from "@/lib/utils";
import { HierarchicalOptionsEditor, FormFieldOption } from "./HierarchicalOptionsEditor";
import { FieldDependencyEditor, FormFieldDependency } from "./FieldDependencyEditor";

const fieldSchema = z.object({
  nome: z.string().min(1, "Nome é obrigatório"),
  slug: z.string().min(1, "Slug é obrigatório"),
  tipo: z.enum([
    "text",
    "textarea",
    "select",
    "multiselect",
    "number",
    "date",
    "datetime",
    "email",
    "tel",
    "checkbox",
    "radio",
    "file",
    "image",
  ]),
  obrigatorio: z.boolean().default(false),
  placeholder: z.string().optional(),
  descricao: z.string().optional(),
  permite_sub_opcoes: z.boolean().default(false),
  modifica_valor_produto: z.boolean().default(false),
});

type FormFieldData = z.infer<typeof fieldSchema>;

export type FormFieldWithOptions = FormFieldData & {
  id?: string;
  form_id?: string;
  options?: FormFieldOption[];
  dependency?: FormFieldDependency | null;
  modifica_valor_produto?: boolean;
  ordem?: number;
};

interface FormFieldEditorProps {
  field?: FormFieldWithOptions;
  allFields?: Array<{
    id: string;
    nome: string;
    slug: string;
    tipo: string;
    options?: FormFieldOption[];
    modifica_valor_produto?: boolean;
  }>;
  onSave: (field: FormFieldData & { options?: FormFieldOption[]; dependency?: FormFieldDependency | null }) => void;
  onCancel: () => void;
  onDelete?: () => void;
  onDisableOtherFields?: () => void;
}

export function FormFieldEditor({
  field,
  allFields = [],
  onSave,
  onCancel,
  onDelete,
  onDisableOtherFields,
}: FormFieldEditorProps) {
  const [options, setOptions] = useState<FormFieldOption[]>(field?.options || []);
  const [allowSubOptions, setAllowSubOptions] = useState(
    field?.permite_sub_opcoes || false
  );
  const [dependency, setDependency] = useState<FormFieldDependency | null>(
    field?.dependency || null
  );

  const form = useForm<FormFieldData>({
    resolver: zodResolver(fieldSchema),
    defaultValues: {
      nome: field?.nome || "",
      slug: field?.slug || "",
      tipo: field?.tipo || "text",
      obrigatorio: field?.obrigatorio || false,
      placeholder: field?.placeholder || "",
      descricao: field?.descricao || "",
      permite_sub_opcoes: field?.permite_sub_opcoes || false,
      modifica_valor_produto: field?.modifica_valor_produto || false,
    },
  });

  useEffect(() => {
    if (field) {
      form.reset({
        nome: field.nome,
        slug: field.slug,
        tipo: field.tipo,
        obrigatorio: field.obrigatorio,
        placeholder: field.placeholder || "",
        descricao: field.descricao || "",
        permite_sub_opcoes: field.permite_sub_opcoes,
        modifica_valor_produto: field.modifica_valor_produto || false,
      });
      setOptions(field.options || []);
      setAllowSubOptions(field.permite_sub_opcoes || false);
    }
  }, [field, form]);

  // Gerar slug automaticamente a partir do nome
  const handleNomeChange = (value: string) => {
    form.setValue("nome", value);
    if (!field || !form.getValues("slug")) {
      const slug = slugify(value);
      form.setValue("slug", slug);
    }
  };

  const handleTipoChange = (tipo: string) => {
    form.setValue("tipo", tipo as FormFieldData["tipo"]);
    // Se não for select/multiselect/radio, limpar opções
    if (!["select", "multiselect", "radio"].includes(tipo)) {
      setOptions([]);
      setAllowSubOptions(false);
      form.setValue("permite_sub_opcoes", false);
    }
  };

  const onSubmit = (data: FormFieldData) => {
    const fieldData = {
      ...data,
      permite_sub_opcoes: allowSubOptions,
      options: ["select", "multiselect", "radio"].includes(data.tipo) ? options : [],
      dependency: ["select", "multiselect", "radio"].includes(data.tipo) ? dependency : null,
      modifica_valor_produto: data.modifica_valor_produto || false,
    };
    
    console.log("[DEBUG] FormFieldEditor - Salvando campo", {
      fieldNome: data.nome,
      fieldTipo: data.tipo,
      optionsCount: fieldData.options?.length || 0,
      hasDependency: !!fieldData.dependency,
      dependency: fieldData.dependency,
      dependencyDependsOnFieldId: fieldData.dependency?.depends_on_field_id,
      dependencyOptionRelationsCount: fieldData.dependency?.option_relations?.length || 0,
      dependencyOptionRelations: fieldData.dependency?.option_relations,
      modificaValorProduto: fieldData.modifica_valor_produto,
    });
    
    // Se está ativando modifica_valor_produto, desativar nos outros campos
    if (fieldData.modifica_valor_produto && onDisableOtherFields) {
      onDisableOtherFields();
    }
    
    onSave(fieldData);
  };

  const fieldType = form.watch("tipo");
  const showOptions = ["select", "multiselect", "radio"].includes(fieldType);
  
  // Verificar se algum outro campo já tem modifica_valor_produto ativo
  const hasOtherFieldWithProductPrice = allFields.some(
    (f) => {
      // Se estamos editando um campo existente, comparar por ID
      if (field?.id) {
        return f.id !== field.id && f.modifica_valor_produto && f.tipo === "select";
      }
      // Se é um campo novo, verificar todos os outros campos
      return f.modifica_valor_produto && f.tipo === "select";
    }
  );
  
  // O switch está desabilitado se outro campo já está ativo E este campo não está ativo
  const isModificaValorProdutoDisabled = hasOtherFieldWithProductPrice && !form.watch("modifica_valor_produto");

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="nome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome do Campo</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) => handleNomeChange(e.target.value)}
                    placeholder="Ex: Escola"
                  />
                </FormControl>
                {/* Identificador abaixo do nome, cinza, pequeno, não editável */}
                <div className="text-xs text-muted-foreground mt-1">
                  Identificador: {form.watch("slug") || "será gerado automaticamente"}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="tipo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Campo</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleTipoChange(value);
                  }}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="text">Texto</SelectItem>
                    <SelectItem value="textarea">Área de Texto</SelectItem>
                    <SelectItem value="select">Seleção Única</SelectItem>
                    <SelectItem value="multiselect">Seleção Múltipla</SelectItem>
                    <SelectItem value="number">Número</SelectItem>
                    <SelectItem value="date">Data</SelectItem>
                    <SelectItem value="datetime">Data e Hora</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="tel">Telefone</SelectItem>
                    <SelectItem value="checkbox">Checkbox</SelectItem>
                    <SelectItem value="radio">Radio</SelectItem>
                    <SelectItem value="file">Arquivo</SelectItem>
                    <SelectItem value="image">Imagem</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="placeholder"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Placeholder</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Texto de exemplo" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-2">
          <FormField
            control={form.control}
            name="descricao"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição / Texto de Ajuda</FormLabel>
                <div className="grid grid-cols-[1fr_auto_auto] gap-4 items-center">
                  <FormControl>
                    <Input {...field} placeholder="Texto explicativo para o usuário" />
                  </FormControl>
                  {fieldType === "select" && (
                    <FormField
                      control={form.control}
                      name="modifica_valor_produto"
                      render={({ field: modificaValorField }) => (
                        <div className="flex flex-row items-center gap-0">
                          <Switch
                            checked={modificaValorField.value}
                            onCheckedChange={modificaValorField.onChange}
                            disabled={isModificaValorProdutoDisabled}
                            className="!mr-[0px] !shrink-0 !p-0 !m-0 !w-8"
                            style={{ marginRight: '0px' }}
                          />
                          <span 
                            onClick={() => !isModificaValorProdutoDisabled && modificaValorField.onChange(!modificaValorField.value)}
                            className={`text-sm font-medium leading-none whitespace-nowrap !ml-0 !pl-0 ${
                              isModificaValorProdutoDisabled 
                                ? 'text-muted-foreground cursor-not-allowed' 
                                : 'cursor-pointer'
                            }`}
                          >
                            Modificar valor do produto
                            {isModificaValorProdutoDisabled && (
                              <span className="text-xs text-muted-foreground block">
                                (Já ativo em outro campo)
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    />
                  )}
                  <FormField
                    control={form.control}
                    name="obrigatorio"
                    render={({ field: obrigatorioField }) => (
                      <div className="flex flex-row items-center gap-0">
                        <Switch
                          checked={obrigatorioField.value}
                          onCheckedChange={obrigatorioField.onChange}
                          className="!mr-[0px] !shrink-0 !p-0 !m-0 !w-8"
                          style={{ marginRight: '0px' }}
                        />
                        <span 
                          onClick={() => obrigatorioField.onChange(!obrigatorioField.value)}
                          className="cursor-pointer text-sm font-medium leading-none whitespace-nowrap !ml-0 !pl-0"
                        >
                          Campo obrigatório
                        </span>
                      </div>
                    )}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {showOptions && (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Opções do Campo</CardTitle>
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="allow-sub-options">Permitir sub-opções</Label>
                    <Switch
                      id="allow-sub-options"
                      checked={allowSubOptions}
                      onCheckedChange={(checked) => {
                        setAllowSubOptions(checked);
                        form.setValue("permite_sub_opcoes", checked);
                      }}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <HierarchicalOptionsEditor
                  fieldId={field?.id || "new"}
                  options={options}
                  onOptionsChange={setOptions}
                  allowSubOptions={allowSubOptions}
                  showProductPrice={fieldType === "select" && form.watch("modifica_valor_produto")}
                />
              </CardContent>
            </Card>

            <FieldDependencyEditor
              currentFieldId={field?.id}
              currentFieldOptions={options}
              allFields={allFields}
              dependency={dependency}
              onDependencyChange={setDependency}
              onOptionsChange={setOptions}
            />
          </>
        )}

        <div className="flex justify-end gap-2 pr-4">
          {onDelete && (
            <Button type="button" variant="destructive" onClick={onDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit">Salvar Campo</Button>
        </div>
      </form>
    </Form>
  );
}

